import {
  BadgeCheck,
  ChevronLeft,
  ChevronRight,
  FileCheck,
  FileQuestion,
  FileText,
  Link2,
  Loader2,
  RefreshCw,
  SearchX,
  Users,
} from 'lucide-react'
import type { FormEvent, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { IdentityAvatar } from '@/components/identities/IdentityAvatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { SearchInput } from '@/components/ui/search-input'
import { cn } from '@/utils/cn'
import { scoreColor } from '@/utils/score-color'

export interface IdentityStats {
  totalPersons: number
  withVerifiedDocuments: number
  /** Possible document-holder links, not face-matching candidates. */
  withPossibleDocuments: number
}

export interface PersonWithDocuments {
  id: string
  name: string
  nickname?: string
  photoPath?: string
  personalNumber?: string
  birthDate?: string
  isVerified: boolean
  documentCount: number
  relationshipCount: number
  idDocumentCount: number
  score: number
}

export interface IdentityManagerViewProps {
  summary: { stats: IdentityStats | null; isLoading: boolean }
  listing: {
    persons: PersonWithDocuments[]
    isLoading: boolean
    searchQuery: string
    currentPage: number
    totalPages: number
    filteredTotal: number
    pageSize: number
  }
  actions: {
    renderPersonActions: (personId: string) => ReactNode
    onSearchChange: (value: string) => void
    onSearchSubmit: () => void
    onRefresh: () => void
    onPersonClick: (personId: string) => void
    onPreviousPage: () => void
    onNextPage: () => void
  }
  /** Advanced Face Detection is on (ADR-0051): the hints mention faces and photos. */
  faceDetection?: boolean
}

/** The directory's hint keys: the face ones only while Advanced Face Detection is on. */
function hintKeys(faceDetection: boolean) {
  const base = faceDetection ? 'identity' : 'identity.withoutFaces'
  return {
    profile: `${base}.profileHint`,
    directory: `${base}.directoryHint`,
    empty: `${base}.emptyHint`,
  }
}

function SummaryTile({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof Users
  label: string
  value: number | null
  hint: string
}) {
  return (
    <Card className="flex flex-col items-start gap-2 px-3 py-3 sm:flex-row sm:gap-3 sm:px-4 sm:py-4">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground sm:size-9">
        <Icon className="size-4" aria-hidden />
      </div>
      <div className="min-w-0">
        <p className="text-2xs font-medium leading-tight text-muted-foreground sm:text-xs">
          {label}
        </p>
        <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">
          {value === null ? '—' : value.toLocaleString()}
        </p>
        <p className="mt-1 hidden text-xs text-muted-foreground sm:block">{hint}</p>
      </div>
    </Card>
  )
}

function ScorePips({ score }: { score: number }) {
  const { t } = useTranslation()
  return (
    <div
      className="flex items-center gap-1"
      role="img"
      aria-label={t('identity.scoreOf', { score, max: 5 })}
    >
      {Array.from({ length: 5 }, (_, i) => (
        <span
          key={i}
          className={cn('h-1.5 w-2 rounded-full', i < score ? scoreColor(score) : 'bg-muted')}
        />
      ))}
      <span className="ml-1 text-xs font-medium tabular-nums text-muted-foreground">{score}/5</span>
    </div>
  )
}

function IdentityRow({
  person,
  actions,
  profileHint,
}: {
  person: PersonWithDocuments
  actions: IdentityManagerViewProps['actions']
  /** Shown when the person has neither a nickname nor a personal number. */
  profileHint: string
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-3 px-4 py-3 transition-colors hover:bg-muted/30 sm:flex-nowrap">
      <button
        type="button"
        onClick={() => actions.onPersonClick(person.id)}
        aria-label={t('identity.openProfileOf', { name: person.name })}
        className="group flex min-w-0 flex-1 basis-full items-center gap-3 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:basis-auto"
      >
        <span className="relative size-11 shrink-0 overflow-hidden rounded-xl border border-border bg-muted">
          <IdentityAvatar
            photoPath={person.photoPath}
            name={person.name}
            fallbackIconClassName="size-5"
          />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-semibold text-foreground group-hover:text-primary">
              {person.name}
            </span>
            {person.isVerified ? (
              <BadgeCheck
                className="size-4 shrink-0 text-success"
                aria-label={t('identity.verified')}
              />
            ) : null}
          </span>
          <span className="truncate text-xs text-muted-foreground">
            {person.nickname && person.nickname !== person.name
              ? person.nickname
              : person.personalNumber || profileHint}
          </span>
        </span>
        <ChevronRight
          className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground"
          aria-hidden
        />
      </button>
      <div className="flex min-w-0 flex-1 items-center gap-4 pl-14 sm:flex-none sm:gap-5 sm:pl-0">
        <div className="hidden lg:block">
          <ScorePips score={person.score} />
        </div>
        <span
          className="inline-flex items-center gap-1.5 text-xs tabular-nums text-muted-foreground"
          title={t('cases.investigate.identity.columns.docs')}
        >
          <FileText className="size-4" aria-hidden />
          <span className="font-medium text-foreground">{person.documentCount}</span>
        </span>
        <span
          className="inline-flex items-center gap-1.5 text-xs tabular-nums text-muted-foreground"
          title={t('cases.investigate.identity.columns.rels')}
        >
          <Link2 className="size-4" aria-hidden />
          <span className="font-medium text-foreground">{person.relationshipCount}</span>
        </span>
        <div
          className="ml-auto"
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
        >
          {actions.renderPersonActions(person.id)}
        </div>
      </div>
    </div>
  )
}

/** Pure case identity directory. Fetching, pagination and navigation live in IdentityManager. */
export function IdentityManagerView({
  summary,
  listing,
  actions,
  faceDetection = false,
}: IdentityManagerViewProps) {
  const { t } = useTranslation()
  const hints = hintKeys(faceDetection)
  const { persons, isLoading, searchQuery, currentPage, totalPages, filteredTotal, pageSize } =
    listing
  const handleSearch = (event: FormEvent) => {
    event.preventDefault()
    actions.onSearchSubmit()
  }

  return (
    <div className="space-y-7">
      <section aria-label={t('identity.overviewLabel')} className="grid grid-cols-3 gap-2 sm:gap-3">
        <SummaryTile
          icon={Users}
          label={t('identity.totalIdentities')}
          value={summary.isLoading ? null : (summary.stats?.totalPersons ?? 0)}
          hint={t('identity.personNodesInGraph')}
        />
        <SummaryTile
          icon={FileCheck}
          label={t('identity.verifiedIdDocs')}
          value={summary.isLoading ? null : (summary.stats?.withVerifiedDocuments ?? 0)}
          hint={t('identity.withHasIdDoc')}
        />
        <SummaryTile
          icon={FileQuestion}
          label={t('identity.possibleDocuments')}
          value={summary.isLoading ? null : (summary.stats?.withPossibleDocuments ?? 0)}
          hint={t('identity.withPossibleHolder')}
        />
      </section>

      <section aria-labelledby="identity-directory-heading" className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 id="identity-directory-heading" className="text-lg font-semibold text-foreground">
              {t('identity.directoryTitle')}
            </h2>
            <p className="text-sm text-muted-foreground">{t(hints.directory)}</p>
          </div>
          <Badge variant="muted">{t('identity.results', { count: filteredTotal })}</Badge>
        </div>
        <form onSubmit={handleSearch} role="search" className="flex items-center gap-2">
          <SearchInput
            containerClassName="min-w-0 flex-1"
            className="max-md:text-ellipsis max-md:placeholder:text-sm"
            placeholder={t('identity.searchPlaceholder')}
            value={searchQuery}
            onValueChange={actions.onSearchChange}
          />
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            onClick={actions.onRefresh}
            aria-label={t('identity.refresh')}
          >
            <RefreshCw className="size-4" />
          </Button>
        </form>

        <Card className="overflow-hidden p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16" role="status">
              <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden />
              <span className="sr-only">{t('identity.loading')}</span>
            </div>
          ) : persons.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
              <SearchX className="size-7 text-muted-foreground" aria-hidden />
              <p className="text-sm font-medium">
                {t(searchQuery ? 'identity.noSearchResults' : 'identity.noIdentities')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(searchQuery ? 'identity.searchHint' : hints.empty)}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border/70">
              {persons.map(person => (
                <IdentityRow
                  key={person.id}
                  person={person}
                  actions={actions}
                  profileHint={t(hints.profile)}
                />
              ))}
            </div>
          )}
        </Card>

        {totalPages > 1 ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {t('identity.pageResults', {
                from: (currentPage - 1) * pageSize + 1,
                to: Math.min(currentPage * pageSize, filteredTotal),
                total: filteredTotal,
              })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={actions.onPreviousPage}
                disabled={currentPage === 1}
                aria-label={t('identity.unassigned.previous')}
              >
                <ChevronLeft className="size-4" />
              </Button>
              <span className="text-xs tabular-nums text-muted-foreground">
                {currentPage} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={actions.onNextPage}
                disabled={currentPage === totalPages}
                aria-label={t('identity.unassigned.next')}
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  )
}
